/**
 * Auth Redirect Utility
 * Handles production redirection to auth.molochain.com for SSO
 * 
 * SSO CENTRALIZATION - Phase 1: Redirect Enforcement
 * Last Updated: 2026-02-01
 */

const AUTH_SUBDOMAIN = 'auth.molochain.com';
const PRODUCTION_DOMAIN = 'molochain.com';

const ALLOWED_SUBDOMAINS = [
  'molochain.com',
  'www.molochain.com',
  'auth.molochain.com',
  'admin.molochain.com',
  'opt.molochain.com',
  'mololink.molochain.com',
  'app.molochain.com',
  'api.molochain.com',
  'rayanava.molochain.com'
];

export function shouldRedirectToAuthPortal(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('replit.dev') ||
    hostname.includes('replit.app')
  ) {
    return false;
  }
  
  if (hostname.startsWith('admin.')) {
    return false;
  }
  
  if (hostname.endsWith(PRODUCTION_DOMAIN) && !hostname.startsWith('auth.')) {
    return true;
  }
  
  return false;
}

export function isValidReturnUrl(returnUrl: string): boolean {
  if (!returnUrl || typeof returnUrl !== 'string') {
    return false;
  }

  const normalized = returnUrl.trim().toLowerCase();
  if (normalized.startsWith('javascript:') || 
      normalized.startsWith('data:') || 
      normalized.startsWith('vbscript:')) {
    return false;
  }

  try {
    const url = new URL(returnUrl.trim());

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return false;
    }

    if (url.protocol === 'http:' && !['localhost', '127.0.0.1'].includes(url.hostname)) {
      return false;
    }

    const hostname = url.hostname.toLowerCase();

    if (ALLOWED_SUBDOMAINS.includes(hostname)) {
      return true;
    }

    if (hostname.endsWith(`.${PRODUCTION_DOMAIN}`)) {
      const subdomain = hostname.slice(0, -PRODUCTION_DOMAIN.length - 1);
      if (subdomain.length > 0 && /^[a-z0-9-]+$/.test(subdomain)) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

export function sanitizeReturnUrl(returnUrl: string | null | undefined): string {
  if (!returnUrl || !isValidReturnUrl(returnUrl)) {
    return `https://${PRODUCTION_DOMAIN}/dashboard`;
  }
  return returnUrl;
}

export function getAuthPortalUrl(path: string, returnUrl?: string): string {
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  let url = `${protocol}//${AUTH_SUBDOMAIN}${path}`;
  
  if (returnUrl) {
    const sanitized = sanitizeReturnUrl(returnUrl);
    url += `?returnUrl=${encodeURIComponent(sanitized)}`;
  }
  
  return url;
}

export function redirectToAuthPortal(path: string = '/login'): void {
  if (typeof window === 'undefined') return;
  
  const protocol = window.location.protocol;
  const currentSearch = window.location.search;
  
  if (path === '/reset-password' || path === '/forgot-password') {
    const authUrl = `${protocol}//${AUTH_SUBDOMAIN}${path}${currentSearch}`;
    window.location.href = authUrl;
    return;
  }
  
  const currentUrl = window.location.href;
  let returnUrl: string;
  
  if (currentUrl.includes('/login') || currentUrl.includes('/register')) {
    const hostname = window.location.hostname;
    if (hostname.startsWith('admin.')) {
      returnUrl = window.location.origin + '/admin';
    } else {
      returnUrl = window.location.origin + '/dashboard';
    }
  } else {
    returnUrl = currentUrl;
  }

  const sanitizedReturnUrl = sanitizeReturnUrl(returnUrl);
  const authUrl = getAuthPortalUrl(path, sanitizedReturnUrl);
  window.location.href = authUrl;
}

export function checkAndRedirectToAuth(pagePath: string): boolean {
  if (shouldRedirectToAuthPortal()) {
    redirectToAuthPortal(pagePath);
    return true;
  }
  return false;
}

export function parseAndValidateReturnUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('returnUrl');
  
  if (!returnUrl) return null;
  
  if (isValidReturnUrl(returnUrl)) {
    return returnUrl;
  }
  
  return null;
}
